import { useId, useState, type FormEvent } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useRouter } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { createTrialRequestSchema, type CreateTrialRequest } from "@ortura/shared";
import { useApi } from "../api/api-context";
import { createTrial, queryKeys } from "../api/platform";
import { CenteredPage } from "../components/centered-page";
import { ErrorMessage } from "../components/error-message";
import { Button } from "../components/ui/button";
import { FieldError, FieldHint, Input, Label } from "../components/ui/form";
import { suggestSubdomain } from "../lib/subdomain";

// Self-Service-Onboarding (Entscheidung 26): eigener Mandant als Testzugang.
export function OnboardingPage() {
  const { t } = useTranslation();
  const api = useApi();
  const queryClient = useQueryClient();
  const router = useRouter();
  const nameId = useId();
  const subdomainId = useId();

  const [name, setName] = useState("");
  const [subdomain, setSubdomain] = useState("");
  // Solange der Kurzname nicht selbst bearbeitet wurde, folgt er dem Namen.
  const [subdomainTouched, setSubdomainTouched] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<{ name?: boolean; subdomain?: boolean }>({});

  // useMutation: für schreibende Aufrufe. Liefert Status (isPending) und Fehler.
  const mutation = useMutation({
    mutationFn: (input: CreateTrialRequest) => createTrial(api, input),
    onSuccess: async (_result, input) => {
      // Mitgliedschaften neu laden, dann in den neuen Mandanten springen.
      await queryClient.invalidateQueries({ queryKey: queryKeys.me });
      router.history.push(`/t/${input.subdomain}`);
    },
  });

  function onSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    // Gleiches Schema wie die API (Entscheidung 36): Fehler schon vor dem Absenden.
    const parsed = createTrialRequestSchema.safeParse({ name, subdomain });
    if (!parsed.success) {
      const paths = new Set(parsed.error.issues.map((issue) => issue.path[0]));
      setFieldErrors({ name: paths.has("name"), subdomain: paths.has("subdomain") });
      return;
    }
    setFieldErrors({});
    mutation.mutate(parsed.data);
  }

  return (
    <CenteredPage wide account>
      <form className="flex flex-col gap-5" onSubmit={onSubmit} noValidate>
        <div className="flex flex-col gap-1">
          <h1 className="text-xl text-ink">{t("onboarding.title")}</h1>
          <p className="text-sm text-ink-muted">{t("onboarding.intro")}</p>
        </div>

        <div className="flex flex-col gap-2">
          <Label htmlFor={nameId}>{t("onboarding.name")}</Label>
          <Input
            id={nameId}
            value={name}
            placeholder={t("onboarding.namePlaceholder")}
            autoComplete="organization"
            aria-invalid={fieldErrors.name}
            onChange={(e) => {
              setName(e.target.value);
              if (!subdomainTouched) setSubdomain(suggestSubdomain(e.target.value));
            }}
          />
          {fieldErrors.name && <FieldError>{t("onboarding.invalidName")}</FieldError>}
        </div>

        <div className="flex flex-col gap-2">
          <Label htmlFor={subdomainId}>{t("onboarding.subdomain")}</Label>
          <Input
            id={subdomainId}
            value={subdomain}
            spellCheck={false}
            aria-invalid={fieldErrors.subdomain}
            onChange={(e) => {
              setSubdomainTouched(true);
              setSubdomain(e.target.value.toLowerCase());
            }}
          />
          <FieldHint>
            {t("onboarding.subdomainHelp", { subdomain: subdomain || "kurzname" })}
          </FieldHint>
          {fieldErrors.subdomain && <FieldError>{t("onboarding.invalidSubdomain")}</FieldError>}
        </div>

        {mutation.error && <ErrorMessage error={mutation.error} />}

        <Button type="submit" size="lg" disabled={mutation.isPending}>
          {t("onboarding.submit")}
        </Button>
      </form>
    </CenteredPage>
  );
}
